import React from 'react'
import { Link } from 'gatsby'
import {
  Layout,
  Header,
  SectionTitle,
  ItemTitle,
  Intro,
} from '../../../../components'

import data from './data.json'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/develop/'}
        state="develop"
        title={data.title}
      />
      <Layout
        category="Overview"
        site={data.site}
        slug={data.slug + '/develop/'}
        state="develop"
        title={data.title}
        complete={true}
      >
        <Intro>
          <p>
            Progress and loading let a user know the level of completion of a
            task or process. Visual indicators should be used when background
            operations are underway or to provide wayfinding as users progress
            through a multi-step process. If you need to communicate details
            about a singular step in the process, consider the{' '}
            <Link
              className="link"
              to={'/' + data.site + '/guidelines/status/develop'}
            >
              status
            </Link>{' '}
            guidelines.
          </p>
          <p>
            Using indicators well has a positive impact on the perceived speed
            of an experience as they make the user focus on progress rather than
            on wait-time. The type of indicator you use will be dependent on
            what the user is waiting for. Follow these guidelines to determine
            which type of indicator is needed.
          </p>
        </Intro>
        <SectionTitle>Types of Indicators</SectionTitle>
        <h3>For user-controlled actions:</h3>
        <p>
          To give the user an idea of how much of a task they have completed,
          use a user-controlled indicator such as the progress bar in the{' '}
          <Link
            className="link"
            to={'/' + data.site + '/patterns/step-guide/develop'}
          >
            step guide
          </Link>{' '}
          pattern. The bar shows linear progress that updates in response to the
          user input.
        </p>
        <div className="loading-bar l-flex-row l-flex-center--xs">
          <div className="p-step-guide u-flex-container l-flex-col--xs-6">
            <div className="p-step-guide__footer">
              <div className="c-progress-bar--step-guide">
                <progress id="progress" value="33" max="100">
                  {' '}
                  33%{' '}
                </progress>
              </div>
              <div className="p-step-guide__footer--left">
                <button
                  id="prevBtn"
                  className="p-step-guide__footer--left-button icon icon--arrow-with-tail-left p-step-guide__footer--left-button-icon"
                  onclick="nextPrev(-1)"
                >
                  <span className="p-step-guide__footer--left-button-text">
                    Back
                  </span>
                </button>
                <span className="p-step-guide__footer-vertical-border"></span>
                <div className="p-step-guide__pagination">
                  <span className="p-step-guide__pagination--first-number">
                    1
                  </span>{' '}
                  of{' '}
                  <span className="p-step-guide__pagination--last-number">
                    3
                  </span>
                </div>
              </div>

              <div className="p-step-guide__footer--right">
                <button
                  id="nextBtn"
                  onclick="nextPrev(1)"
                  className="p-step-guide__button--primary"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
        <h3 className="l-margin-top--xxlarge">
          For system-controlled actions:
        </h3>
        <p>
          When a user initiates a task that requires a back-end call, use one of
          the below system-controlled indicators.
        </p>

        <ItemTitle>Skeleton Loader</ItemTitle>
        <p>
          Skeleton loaders are a sophisticated wait animation that give the user
          an abstract idea of the content they are about to see. use skeleton
          loaders when loading new information in a layout and only when the
          format of the information is known beforehand. If the shape of the
          loader is vastly different from the loaded content, it will cause a
          jarring experience for the user. Do not use skeleton loaders for
          asynchronous or regional loading.
        </p>
        <ItemTitle>Action Indicator</ItemTitle>
        <p>
          Use the full-page variant of the{' '}
          <Link
            className="link"
            to={'/' + data.site + '/components/action-indicator/develop'}
          >
            action indicator
          </Link>{' '}
          component when loading information whose layout is unknown. Instead of
          giving the user a glimpse into the layout of new content, the action
          indicator uses copy to let the user know the system's status. It is
          the only indicator that can explicitly tell the user what is
          happening. The full-page action indicator appears in the center of the
          page or over a specific content region. Use the non-blocking variant
          of the action indicator to let the user know of an asynchronous
          action, such as autosaving.
        </p>
        <div className="l-flex-row l-flex-center--xs">
          <div className="u-border-solid--gray-light u-position--relative l-margin--large u-frame--rectangle-rounded l-margin-bottom--xxxlarge l-flex-col--xs-6">
            <div className="l-padding--large">
              <label for="" className="c-label">
                My Input
              </label>
              <input
                type="text"
                className="c-text-field l-margin-bottom--large"
              />
              <label for="" className="c-label">
                My Other Input
              </label>
              <input
                type="text"
                className="c-text-field l-margin-bottom--large"
              />
              <label for="" className="c-label">
                My Other Other Input
              </label>
              <input
                type="text"
                className="c-text-field l-margin-bottom--xxlarge"
              />
            </div>

            <div className="c-loading__overlay--contained" id="overlay">
              <div className="c-loading" id="loading">
                <div className="c-loading__spinner" id="spinner"></div>
                <div className="c-loading__content" id="content">
                  Loading...
                </div>
              </div>
            </div>
          </div>
        </div>

        <ItemTitle>Ellipsis</ItemTitle>
        <p>
          Use this wait animation only in button loading states or to indicate
          communication in a chat window. Show the loading state of a button
          when the layout will not change to accommodate the new content but
          there is a significant wait for a back-end call (e.g. when submitting
          information in a form) or when new content follows the same pattern as
          previous content (e.g. loading more search results).
        </p>
        <div className="l-flex-row l-flex-center--xs">
          <button className="c-button--primary-loading l-margin-bottom--small l-flex-col--xs-6">
            <span className="c-button--primary-loading__item-1" />
            <span className="c-button--primary-loading__item-2" />
            <span className="c-button--primary-loading__item-3" />
          </button>
        </div>
        <p className="l-margin-top--xxlarge">
          When choosing which loading indicator to use, consider whether the
          layout will change, if the new layout is known or unknown and if you
          need to communicate the specific action with the user.
        </p>
        <hr />
        <SectionTitle>Determinate and Indeterminate Loading</SectionTitle>
        <p>
          Determinate loaders animate based on how long it will take to complete
          a task. Indeterminate loaders animate cyclically to let a user know
          that a task is underway, but give no indication of when the task may
          be completed. When possible, use determinate loaders, especially for
          longer tasks. Using indeterminate loaders for long periods can lead
          users to believe that something has gone wrong.
        </p>
      </Layout>
    </>
  )
}
